import React, { useState, useEffect } from "react";
import { getIn } from "formik";
import {
    Box,
    Button,
    Typography,
    LinearProgress,
    IconButton,
    FormControl,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import DescriptionIcon from "@mui/icons-material/Description";
import CustomDivider from "../../utils/CustomDivider";
import { getFileUrl } from "../../constants";

export const DropzoneUploadLargFileField = ({ field, form, label, fileDetail,text_label }) => {
    const errorText = getIn(form.touched, field.name) && getIn(form.errors, field.name);
    const [file, setFile] = useState(field.value || null);
    const [progress, setProgress] = useState(0);
    useEffect(()=>{
        if(fileDetail?.file_type){
            setProgress(100)
        }
    },[fileDetail])
    const [isUploading, setIsUploading] = useState(false);
    useEffect(() => {
        setFile(field.value || null);
    }, [field.value]);

    // ---------------- CONFIG ----------------
    const MAX_SIZE = 2 * 1024 * 1024; // 2 MB

    const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "application/pdf",
        "video/mp4",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    const allowedExtensions = ".jpeg,.jpg,.png,.pdf,.mp4,.doc,.docx";

    // ---------------- VALIDATION ----------------
    const validateFile = (file) => {
        if (!allowedTypes?.includes(file.type)) {
            return "Only image, PDF, video, DOC or DOCX files are allowed.";
        }
        if (file.size > MAX_SIZE) {
            return "File size must be less than 2 MB.";
        }
        return null;
    };

    // ---------------- UPLOAD SIMULATION ----------------
    const simulateUpload = (selectedFile) => {
        setIsUploading(true);
        setProgress(0);

        const interval = setInterval(() => {
            setProgress((prev) => {
                if (prev >= 100) {
                    clearInterval(interval);
                    setIsUploading(false);
                    form.setFieldValue(field.name, selectedFile);
                    return 100;
                }
                return prev + 10;
            });
        }, 150);
    };

    // ---------------- HANDLERS ----------------
    const handleFileSelect = (event) => {
        const selectedFile = event.target.files?.[0];
        if (!selectedFile) return;

        const validationError = validateFile(selectedFile);

        if (validationError) {
            // form.setFieldError(field.name, validationError);
            form.setFieldTouched(field.name, true, false);
            form.setFieldError(field.name, validationError);
            return;
        }

        setFile(selectedFile);
        simulateUpload(selectedFile);
    };

    const onRemove = () => {
        setFile(null);
        setProgress(0);
        form.setFieldValue(field.name, null);
    };

    // ---------------- ICON / PREVIEW ----------------
    // const renderFilePreview = () => {
    //     if (!file) return null;

    //     // If backend URL string
    //     if (typeof file === "string") {
    //         const url = file.startsWith("http") ? file : `/${file}`;
    //         return (
    //             <img
    //                 src={url}
    //                 alt="file"
    //                 style={{ width: "100%", height: "100%", objectFit: "cover" }}
    //             />
    //         );
    //     }

    //     // Image preview
    //     if (file?.type?.includes("image")) {
    //         return (
    //             <img
    //                 src={URL.createObjectURL(file)}
    //                 alt={file.name}
    //                 style={{ width: "100%", height: "100%", objectFit: "cover" }}
    //             />
    //         );
    //     }

    //     // PDF
    //     if (file.type === "application/pdf") {
    //         return <PictureAsPdfIcon sx={{ fontSize: 40, color: "red" }} />;
    //     }

    //     // Video
    //     if (file.type === "video/mp4") {
    //         return <VideoFileIcon sx={{ fontSize: 40, color: "purple" }} />;
    //     }

    //     // Docs
    //     return <DescriptionIcon sx={{ fontSize: 40, color: "#375EF9" }} />;
    // };

    const renderFilePreview = () => {
        if (!file) return null;

        // ---------- BACKEND EXISTING FILE OBJECT CASE ----------
        if (typeof file === "object" && file.file_type) {

            // Use the same base URL and path-cleaning logic as ViewAllLmsContant.js
            const baseUrl = window._env_?.IMAGE_PATH_NODE || "";
            const rawPath = file.file_path || "";
            const normalized = rawPath.replace(/\\/g, '/');
            const uploadsIndex = normalized.indexOf('uploads/');
            const cleanedPath = uploadsIndex !== -1
                ? '/' + normalized.substring(uploadsIndex)
                : normalized;
            const fullUrl = `${baseUrl}${cleanedPath}`;

            // IMAGE CASE
            if (file.file_type === "image") {
                return (
                    <img
                        src={fullUrl}
                        alt={file.file_name}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                );
            }

            // VIDEO CASE
            if (file.file_type === "video") {
                return <VideoFileIcon sx={{ fontSize: 40, color: "purple" }} />;
            }

            // DOCUMENT CASE
            if (file.file_type === "document") {
                return <PictureAsPdfIcon sx={{ fontSize: 40, color: "red" }} />;
            }
        }

        // ---------- OLD STRING URL CASE ----------
        if (typeof file === "string") {
            // const url = file.startsWith("http") ? file : `/${file}`;
            return (
                <>
                    {fileDetail?.file_type === "document" ?
                       <PictureAsPdfIcon sx={{ fontSize: 40, color: "red" }} /> : <img
                            src={getFileUrl(file)}
                            alt="file"
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />

                    }


                </>

            );
        }

        // ---------- NEWLY SELECTED FILE CASE ----------
        if (file?.type?.includes("image")) {
            return (
                <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
            );
        }

        if (file.type === "application/pdf") {
            return <PictureAsPdfIcon sx={{ fontSize: 40, color: "red" }} />;
        }

        if (file.type === "video/mp4") {
            return <VideoFileIcon sx={{ fontSize: 40, color: "purple" }} />;
        }

        return <DescriptionIcon sx={{ fontSize: 40, color: "#375EF9" }} />;
    };


    // const fileSizeMB =
    //     file && typeof file !== "string"
    //         ? (file.size / (1024 * 1024)).toFixed(2)
    //         : null;

    const fileSizeMB =
        file && file.size
            ? (file.size / (1024 * 1024)).toFixed(2)
            : null;


    // ---------------- UI ----------------
    return (
        <FormControl fullWidth>
            {!file ? (
                <Box
                    sx={{
                        border: "2px dashed #ccc",
                        borderRadius: 2,
                        p: 4,
                        textAlign: "center",
                        "&:hover": { borderColor: "#888" },
                    }}
                >
                    <CloudUploadIcon sx={{ fontSize: 40, color: "#375EF9" }} />
                    <CustomDivider size={10} />

                    <Typography variant="body1">
                        {label || "Choose a file or drag & drop it here"}
                    </Typography>

                    <CustomDivider size={5} />
                    <Typography variant="body2">
                        {text_label ||"Images, PDF, Video, DOC, DOCX (max 2 MB)"}
                    </Typography>

                    <CustomDivider size={20} />

                    <Button
                        variant="outlined"
                        component="label"
                    // sx={{ borderRadius: "2rem" }}
                    >
                        Browse Files
                        <input
                            type="file"
                            hidden
                            accept={allowedExtensions}
                            onChange={handleFileSelect}
                        />
                    </Button>
                </Box>
            ) : (
                <Box
                    sx={{
                        mt: 3,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: "#f7f9fc",
                        position: "relative",
                    }}
                >
                    {/* File Info */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box
                            sx={{
                                width: 60,
                                height: 60,
                                borderRadius: "6px",
                                overflow: "hidden",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            {renderFilePreview()}
                        </Box>

                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {/* {typeof file === "string" ? file.split("/").pop() : file.name} */}
                                {typeof file === "object" && file.file_name
                                    ? file.file_name
                                    : typeof file === "string"
                                        ? file.split("/").pop()
                                        : file.name}

                            </Typography>

                            <Typography variant="body2" >
                                {fileSizeMB && `${fileSizeMB} MB • `}
                                {isUploading ? "Uploading..." : "Completed"}
                            </Typography>
                        </Box>
                    </Box>

                    <CustomDivider size={10} />
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        color="primary"
                        sx={{
                            height: 4,
                            borderRadius: 2,
                            mt: 0.5,
                            width: "100%",
                            "& .MuiLinearProgress-bar": {
                                backgroundColor: "#375EF9",
                            },
                        }}
                    />
                    <IconButton
                        onClick={onRemove}
                        size="small"
                        sx={{
                            position: "absolute",
                            top: 7,
                            right: 8,
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            border: "2px solid #292D32",
                            p: 1.1,
                        }}
                    >
                        <CloseIcon sx={{ width: 18, height: 18, color: "#292D32" }} />
                    </IconButton>
                </Box>
            )}

            {errorText && (
                <Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>
                    {errorText}
                </Typography>
            )}
        </FormControl>
    );
};






